import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Checkbox,
  VStack,
  HStack,
  Badge,
  Divider,
  useDisclosure,
} from '@chakra-ui/react'
import axios from 'axios'

interface Announcement {
  id: number
  title: string
  content: string
  created_at: string
}

const AnnouncementPopup: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [dontShowToday, setDontShowToday] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()

  // 오늘 날짜 문자열 (YYYY-MM-DD)
  const getTodayString = () => {
    const today = new Date()
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  }

  // localStorage에서 오늘 하루 안 보기 체크
  const isDismissedToday = (id: number) => {
    const key = `announcement_dismiss_${id}_${getTodayString()}`
    return localStorage.getItem(key) === 'true'
  }

  // 오늘 하루 안 보기 저장
  const setDismissToday = (id: number) => {
    const key = `announcement_dismiss_${id}_${getTodayString()}`
    localStorage.setItem(key, 'true')
  }

  // 활성 공지사항 조회
  const fetchActiveAnnouncements = async () => {
    try {
      const response = await axios.get('/graph/announcements/popup/active')
      if (response.data.success && response.data.data.length > 0) {
        // 오늘 하루 안 보기로 제외할 공지사항 필터링
        const filtered = response.data.data.filter(
          (announcement: Announcement) => !isDismissedToday(announcement.id)
        )
        
        if (filtered.length > 0) {
          setAnnouncements(filtered)
          setCurrentIndex(0)
          onOpen()
        }
      }
    } catch (error) {
      console.error('팝업 공지사항 조회 실패:', error)
    }
  }

  useEffect(() => {
    // 컴포넌트 마운트 시 활성 공지사항 조회
    fetchActiveAnnouncements()
  }, [])

  const currentAnnouncement = announcements[currentIndex]

  const handleClose = () => {
    if (dontShowToday && currentAnnouncement) {
      setDismissToday(currentAnnouncement.id)
    }
    
    // 다음 공지사항이 있으면 표시
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1)
      setDontShowToday(false)
    } else {
      onClose()
    }
  }

  const handleCloseAll = () => {
    // 모든 공지사항에 오늘 하루 안 보기 적용
    if (dontShowToday) {
      announcements.forEach((announcement) => {
        setDismissToday(announcement.id)
      })
    }
    onClose()
  }

  if (!currentAnnouncement) return null

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      size="lg"
      isCentered
      closeOnOverlayClick={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent mx={4}>
        <ModalHeader>
          <HStack>
            <Badge colorScheme="orange" fontSize="sm">공지</Badge>
            <Text fontSize="lg">{currentAnnouncement.title}</Text>
          </HStack>
          {announcements.length > 1 && (
            <Text fontSize="sm" color="gray.500" fontWeight="normal" mt={1}>
              {currentIndex + 1} / {announcements.length}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <Box
            p={4}
            bg="gray.50"
            borderRadius="md"
            maxH="400px"
            overflowY="auto"
            dangerouslySetInnerHTML={{ __html: currentAnnouncement.content }}
            sx={{
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                margin: '10px 0',
                borderRadius: 'md',
              },
              '& p': {
                marginBottom: '10px',
              },
            }}
          />
          <Text fontSize="xs" color="gray.500" mt={2}>
            등록일: {new Date(currentAnnouncement.created_at).toLocaleDateString()}
          </Text>
        </ModalBody>

        <Divider />

        <ModalFooter>
          <VStack w="full" spacing={3}>
            <HStack w="full" justify="space-between">
              <Checkbox
                isChecked={dontShowToday}
                onChange={(e) => setDontShowToday(e.target.checked)}
                size="sm"
              >
                <Text fontSize="sm">오늘 하루 안 보기</Text>
              </Checkbox>
              <HStack>
                {announcements.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={handleCloseAll}>
                    모두 닫기
                  </Button>
                )}
                <Button colorScheme="bexco" size="sm" onClick={handleClose}>
                  {currentIndex < announcements.length - 1 ? '다음' : '확인'}
                </Button>
              </HStack>
            </HStack>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AnnouncementPopup
